export const MAX_LEVEL = 100;
export const MAX_PRESTIGE = 5;
export const XP_PER_MINUTE = 2;

// Level names organized by tiers (every 10 levels)
// Both classes share the first 10 levels, then diverge

const ELF_TIERS: Record<number, string[]> = {
  // Tier 1: Seeds (1-10)
  1: ["🌱 Tomato Seed", "🪴 Fearless Sprout", "🌿 Young Seedling", "🍃 Ethereal Plant", "🌾 Growing Vine",
    "🌺 Enlightened Blossom", "🌸 Tomato Blossom", "🌼 Archer Bloom", "🌻 Radiant Plant", "🍀 Mature Plant"],
  // Tier 2: Guardians (11-20)
  2: ["🌷 Forest Guardian", "🌹 Nature Keeper", "💐 Grove Protector", "🥀 Woodland Champion", "🏵️ Elf Master",
    "🍃 Forest Lord", "🌿 Ancient Sage", "✨ Mystic Legend", "🌙 Lunar Spirit", "⭐ Starlight Ascendant"],
  // Tier 3: Mystics (21-30)
  3: ["🔮 Arcane Initiate", "💫 Celestial Adept", "🌙 Moon Walker", "☀️ Sun Dancer", "⭐ Star Gazer",
    "🌟 Cosmic Seeker", "💎 Crystal Seer", "👑 Mystic Ruler", "🏆 Ethereal Champion", "✨ Astral Master"],
  // Tier 4: Spirits (31-40)
  4: ["🦋 Spirit Touched", "🌊 Wave Whisperer", "🌪️ Wind Caller", "❄️ Frost Weaver", "🔥 Flame Dancer",
    "🌈 Prism Walker", "🎭 Shadow Mage", "🦌 Beast Friend", "🦅 Sky Watcher", "🐉 Dragon Kin"],
  // Tier 5: Legends (41-50)
  5: ["🌌 Void Walker", "💠 Dimension Sage", "🎆 Reality Bender", "🌠 Astral Guide", "🔱 Divine Spirit",
    "⚜️ Sacred Keeper", "🏛️ Temple Mystic", "📿 Enlightened Soul", "🕊️ Transcendent Being", "👑 Legendary Elf"],
  // Tier 6: Ancients (51-60)
  6: ["🌳 Ancient Root", "🍂 Timeless Leaf", "🌲 Eternal Pine", "🎋 Wisdom Bamboo", "🪷 Lotus Elder",
    "🌸 Cherry Sage", "🌺 Hibiscus Master", "🪻 Iris Keeper", "🌹 Rose Guardian", "🌻 Sunflower Oracle"],
  // Tier 7: Celestials (61-70)
  7: ["🌙 Moon Child", "☀️ Sun Bearer", "⭐ Star Born", "🌟 Nova Heart", "💫 Comet Rider",
    "🌌 Galaxy Walker", "🪐 Planet Keeper", "☄️ Meteor Soul", "🌠 Shooting Star", "✨ Cosmic Elder"],
  // Tier 8: Demigods (71-80)
  8: ["🔮 Oracle Prime", "💎 Diamond Soul", "👁️ All-Seeing One", "🏺 Ancient Vessel", "📜 Scroll Keeper",
    "🗝️ Key Master", "⚱️ Urn Guardian", "🎭 Mask Bearer", "🪬 Charm Holder", "🔯 Symbol Master"],
  // Tier 9: Divine (81-90)
  9: ["🕊️ Angel Touched", "👼 Seraph Friend", "🦋 Divine Butterfly", "🌈 Rainbow Bridge", "☁️ Cloud Walker",
    "🌤️ Dawn Bringer", "🌅 Horizon Keeper", "🌄 Mountain Spirit", "🏔️ Peak Elder", "⛰️ Earth Titan"],
  // Tier 10: Gods (91-100)
  10: ["🌍 World Guardian", "🌎 Realm Protector", "🌏 Globe Master", "🪐 Planet Lord", "🌌 Universe Watcher",
    "🔱 Trident Bearer", "⚡ Thunder Chosen", "🌊 Ocean Blessed", "🔥 Fire Eternal", "🍅 Tomato God"],
};

const HUMAN_TIERS: Record<number, string[]> = {
  // Tier 1: Seeds (1-10)
  1: ["🌱 Tomato Seed", "🪴 Brave Sprout", "🌿 Young Seedling", "🍃 Strong Plant", "🌾 Growing Vine",
    "🌺 Battle Blossom", "🌸 Tomato Blossom", "🌼 Warrior Bloom", "🌻 Fighting Plant", "🍀 Veteran Plant"],
  // Tier 2: Warriors (11-20)
  2: ["⚔️ Blade Initiate", "🛡️ Shield Bearer", "🗡️ Sword Wielder", "🏹 Bow Master", "🪓 Axe Champion",
    "🔨 Hammer Lord", "⚒️ Forge Knight", "🎖️ Medal Winner", "👑 War King", "🏆 Battle Ascendant"],
  // Tier 3: Champions (21-30)
  3: ["⚔️ Steel Heart", "🛡️ Iron Wall", "🗡️ Blade Dancer", "🏹 Arrow Storm", "🪓 Bone Crusher",
    "🔨 Earth Shaker", "⚒️ Metal Master", "🎖️ Glory Seeker", "👑 Crown Bearer", "🏆 Arena Legend"],
  // Tier 4: Conquerors (31-40)
  4: ["🔥 Flame Warrior", "⚡ Thunder Knight", "🌊 Sea Raider", "🌪️ Storm Bringer", "❄️ Ice Warlord",
    "🌈 Victory Seeker", "🎭 Shadow Striker", "🦅 Eagle Leader", "🐺 Wolf Commander", "🦁 Lion Conqueror"],
  // Tier 5: Legends (41-50)
  5: ["🌌 Realm Breaker", "💠 Dimension Warrior", "🎆 World Conquerer", "🌠 Star Slayer", "🔱 Divine Fighter",
    "⚜️ Sacred Warrior", "🏛️ Temple Champion", "📿 Blessed Blade", "🕊️ Immortal Fighter", "👑 Legendary Human"],
  // Tier 6: Titans (51-60)
  6: ["🗿 Stone Titan", "🏔️ Mountain Crusher", "⛰️ Peak Conqueror", "🌋 Volcano Lord", "🏜️ Desert King",
    "🏝️ Island Ruler", "🌲 Forest Ravager", "🌊 Ocean Master", "❄️ Glacier Lord", "🔥 Inferno Titan"],
  // Tier 7: Warlords (61-70)
  7: ["⚔️ Grand Marshal", "🛡️ Supreme Defender", "🗡️ Blade Emperor", "🏹 Arrow Emperor", "🪓 Axe Emperor",
    "🔨 Hammer Emperor", "⚒️ Forge Emperor", "🎖️ Medal Emperor", "👑 War Emperor", "🏆 Battle Emperor"],
  // Tier 8: Demigods (71-80)
  8: ["🔱 Trident Wielder", "⚡ Lightning Born", "🌊 Tsunami Caller", "🔥 Inferno Heart", "❄️ Blizzard Soul",
    "🌪️ Cyclone Master", "🌋 Magma Lord", "💎 Diamond Fist", "👁️ Fear Bringer", "🦴 Death Walker"],
  // Tier 9: Divine (81-90)
  9: ["⚔️ God Slayer", "🛡️ Heaven's Shield", "🗡️ Divine Blade", "🏹 Celestial Arrow", "🪓 Titan's Axe",
    "🔨 Thunder Hammer", "⚒️ Cosmic Forge", "🎖️ Eternal Glory", "👑 aImmortal King", "🏆 Legendary Lord"],
  // Tier 10: Gods (91-100)
  10: ["🌍 World Conqueror", "🌎 Realm Crusher", "🌏 Globe Slayer", "🪐 Planet Breaker", "🌌 Universe Warrior",
    "🔱 Poseidon's Heir", "⚡ Zeus's Champion", "🔥 Hades's Chosen", "⚔️ Ares's Blood", "🍅 Tomato God"],
};

// Generate full level name maps from tiers
function generateLevelNames(tiers: Record<number, string[]>): Record<number, string> {
  const names: Record<number, string> = {};
  for (let tier = 1; tier <= 10; tier++) {
    const tierNames = tiers[tier];
    for (let i = 0; i < 10; i++) {
      const level = (tier - 1) * 10 + i + 1;
      names[level] = tierNames[i];
    }
  }
  return names;
}

export const LEVEL_NAMES_ELF = generateLevelNames(ELF_TIERS);
export const LEVEL_NAMES_HUMAN = generateLevelNames(HUMAN_TIERS);

export const ROLE_EMOJI_ELF = "🏹";
export const ROLE_EMOJI_HUMAN = "⚔️";

export function getLevelName(level: number, path: 'elf' | 'human'): string {
  const names = path === 'elf' ? LEVEL_NAMES_ELF : LEVEL_NAMES_HUMAN;
  return names[level] || names[MAX_LEVEL];
}

/**
 * Calculate XP needed for a specific level using logarithmic curve
 * Formula: 100 × (1 + log2(level))
 * 
 * This creates a curve where early levels are easier and later levels
 * require progressively more XP, but not exponentially more.
 * 
 * Examples:
 * - Level 1→2: 100 XP
 * - Level 10→11: 432 XP
 * - Level 50→51: 664 XP
 * - Level 99→100: 764 XP
 * 
 * Total XP for 1 prestige (100 levels): ~42,000 XP
 * At 5 hrs/day (600 XP): ~70 days per prestige (~2.3 months)
 * Max prestige (5) reached in: ~350 days (~1 year)
 */
export function getXPNeeded(level: number): number {
  return Math.floor(100 * (1 + Math.log2(Math.max(1, level))));
}

export function getTotalXPForLevel(level: number): number {
  let total = 0;
  for (let i = 1; i < level; i++) {
    total += getXPNeeded(i);
  }
  return total;
}

/**
 * Calculate total XP needed for one full prestige (levels 1-100)
 */
export function getXPPerPrestige(): number {
  return getTotalXPForLevel(MAX_LEVEL + 1);  // XP to complete level 100
}

/**
 * Calculate remaining XP within current level from total XP
 * Used for progress bar display
 * 
 * @param totalXP Total XP stored in database
 * @param level Current level (already calculated by DB trigger)
 * @param prestigeLevel Current prestige level (already calculated by DB trigger)
 * @returns XP progress within current level (0 to getXPNeeded(level)-1)
 */
export function getRemainingXP(totalXP: number, level: number, prestigeLevel: number): number {
  // Calculate XP used by completed prestiges
  const xpPerPrestige = getXPPerPrestige();
  const xpFromPrestiges = prestigeLevel * xpPerPrestige;

  // Calculate XP used by completed levels in current prestige
  const xpFromLevels = getTotalXPForLevel(level);

  // Remaining XP is total minus completed
  return Math.max(0, totalXP - xpFromPrestiges - xpFromLevels);
}

export function getBadgeForLevel(level: number, _prestigeLevel: number): string {
  if (level <= 10) return "🌱";
  if (level <= 20) return "🌿";
  if (level <= 30) return "⭐";
  if (level <= 40) return "🔥";
  if (level <= 50) return "👑";
  if (level <= 60) return "🌳";
  if (level <= 70) return "🌙";
  if (level <= 80) return "💎";
  if (level <= 90) return "🕊️";
  return "🍅";
}

